import React from 'react';
import { PlayOutput, LeaguePlayer } from '../../generated-api';
import { comparePlayersByRank } from '../../utils/playerRanking';
import { ArrowUpIcon, ArrowDownIcon, BoltIcon, TrophyIcon, FireIcon, StarIcon } from '@heroicons/react/24/solid';

// ── RecentFormIndicator ──────────────────────────────────────────────

interface RecentFormIndicatorProps {
  recentPlays: PlayOutput[];
  playerId: string;
  count?: number;
}

export const RecentFormIndicator: React.FC<RecentFormIndicatorProps> = ({
  recentPlays,
  playerId,
  count = 5,
}) => {
  const results: ('win' | 'loss' | 'none')[] = recentPlays.slice(0, count).map(play => {
    // Winner = team at ranking[0]
    const winningTeam = typeof play.ranking[0] === 'string' ? play.ranking[0] : play.ranking[0]?.[0];
    if (!winningTeam) return 'none' as const;
    const winningPlayerIds = play.teams[winningTeam] || [];
    return winningPlayerIds.includes(playerId) ? 'win' as const : 'loss' as const;
  });

  // Pad with 'none' if fewer than count
  while (results.length < count) {
    results.push('none');
  }

  return (
    <div className="flex items-center gap-1">
      {results.map((result, i) => (
        <div
          key={i}
          className={`w-2.5 h-2.5 rounded-full ${
            result === 'win'
              ? 'bg-success-500'
              : result === 'loss'
              ? 'bg-error-500'
              : 'bg-surface-skeleton'
          }`}
          title={result === 'none' ? 'No data' : result}
        />
      ))}
    </div>
  );
};

// ── StreakDisplay ─────────────────────────────────────────────────────

interface StreakDisplayProps {
  recentPlays: PlayOutput[];
  playerId: string;
}

export const StreakDisplay: React.FC<StreakDisplayProps> = ({ recentPlays, playerId }) => {
  if (recentPlays.length === 0) return null;

  // Determine streak type from most recent play
  const firstResult = getPlayResult(recentPlays[0], playerId);
  if (firstResult === 'unknown') return null;

  let streakCount = 0;
  for (const play of recentPlays) {
    if (getPlayResult(play, playerId) === firstResult) {
      streakCount++;
    } else {
      break;
    }
  }

  if (streakCount < 2 || firstResult !== 'win') return null;

  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-success-50 text-success-700">
      {streakCount >= 3 && <FireIcon className="h-3.5 w-3.5 text-orange-500" />}
      {streakCount} W streak
    </span>
  );
};

// ── UpsetAlert ───────────────────────────────────────────────────────

interface UpsetAlertProps {
  winnerProbability: number;
}

export const UpsetAlert: React.FC<UpsetAlertProps> = ({ winnerProbability }) => {
  if (winnerProbability >= 30) return null;

  const percentage = Math.round(winnerProbability);

  return (
    <div className="rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-3 flex items-center gap-3 shadow-sm">
      <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
        <BoltIcon className="h-5 w-5 text-white" />
      </div>
      <div>
        <div className="font-bold text-sm">Upset!</div>
        <div className="text-xs text-amber-100">
          Winner had only a {percentage}% pre-game chance
        </div>
      </div>
    </div>
  );
};

// ── LeaguePositionChange ─────────────────────────────────────────────

interface LeaguePositionChangeProps {
  playerName: string;
  positionBefore: number;
  positionAfter: number;
}

export const LeaguePositionChange: React.FC<LeaguePositionChangeProps> = ({
  playerName,
  positionBefore,
  positionAfter,
}) => {
  const diff = positionBefore - positionAfter; // positive = moved up

  return (
    <div className="flex items-center py-2.5 gap-3">
      <span className="text-sm font-medium text-heading truncate flex-1 min-w-0">{playerName}</span>
      <span className="text-sm text-muted font-mono w-6 text-right">#{positionBefore}</span>
      <span className="text-muted text-xs">&rarr;</span>
      <span className="text-sm font-semibold text-heading font-mono w-6 text-right">#{positionAfter}</span>
      <span className="w-10 text-right">
        {diff > 0 && (
          <span className="inline-flex items-center gap-0.5 text-xs font-bold text-success-700">
            <ArrowUpIcon className="h-3 w-3" />
            {diff}
          </span>
        )}
        {diff < 0 && (
          <span className="inline-flex items-center gap-0.5 text-xs font-bold text-error-600">
            <ArrowDownIcon className="h-3 w-3" />
            {Math.abs(diff)}
          </span>
        )}
      </span>
    </div>
  );
};

// ── RecapFunFacts ────────────────────────────────────────────────────

interface FunFact {
  icon: string;
  message: string;
}

interface RecapFunFactsProps {
  facts: FunFact[];
}

const FACT_ICON_MAP: Record<string, React.ReactNode> = {
  trophy: <TrophyIcon className="h-5 w-5 text-amber-600" />,
  target: <StarIcon className="h-5 w-5 text-primary-600" />,
  muscle: <FireIcon className="h-5 w-5 text-orange-500" />,
};

function getFactIcon(emojiIcon: string): React.ReactNode {
  if (emojiIcon === '\u{1F3C6}') return FACT_ICON_MAP.trophy;
  if (emojiIcon === '\u{1F3AF}') return FACT_ICON_MAP.target;
  if (emojiIcon === '\u{1F4AA}') return FACT_ICON_MAP.muscle;
  return FACT_ICON_MAP.target;
}

export const RecapFunFacts: React.FC<RecapFunFactsProps> = ({ facts }) => {
  if (facts.length === 0) return null;

  return (
    <div className="space-y-2">
      {facts.map((fact, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-xl bg-gradient-to-r from-amber-50 to-primary-50 px-4 py-3.5 border border-amber-200/60"
        >
          <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/80 flex items-center justify-center shadow-sm">
            {getFactIcon(fact.icon)}
          </div>
          <span className="text-sm font-medium text-heading">{fact.message}</span>
        </div>
      ))}
    </div>
  );
};

// ── RecapSkeleton ────────────────────────────────────────────────────

export const RecapSkeleton: React.FC = () => {
  return (
    <div className="container mx-auto px-4 py-6 animate-pulse space-y-6">
      {/* Header skeleton */}
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-xl bg-surface-skeleton" />
        <div className="flex-1 space-y-2">
          <div className="h-6 bg-surface-skeleton rounded w-48" />
          <div className="h-4 bg-surface-skeleton rounded w-32" />
        </div>
      </div>

      {/* Rating cards skeleton */}
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="rounded-xl border border-edge p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-surface-skeleton" />
              <div className="w-10 h-10 rounded-full bg-surface-skeleton" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-surface-skeleton rounded w-24" />
                <div className="h-4 bg-surface-skeleton rounded w-40" />
              </div>
              <div className="h-7 w-16 bg-surface-skeleton rounded" />
            </div>
          </div>
        ))}
      </div>

      {/* Highlights skeleton */}
      <div className="grid grid-cols-2 gap-3">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="rounded-lg bg-surface-skeleton h-20" />
        ))}
      </div>
    </div>
  );
};

// ── Helpers ──────────────────────────────────────────────────────────

function getPlayResult(play: PlayOutput, playerId: string): 'win' | 'loss' | 'unknown' {
  const winningTeam = typeof play.ranking[0] === 'string' ? play.ranking[0] : play.ranking[0]?.[0];
  if (!winningTeam) return 'unknown';
  const winningPlayerIds = play.teams[winningTeam] || [];
  return winningPlayerIds.includes(playerId) ? 'win' : 'loss';
}

// ── Computation helpers used by PlayRecapPage ────────────────────────

export function computeRecentForm(
  recentPlays: PlayOutput[],
  playerId: string,
  count = 5
): ('win' | 'loss' | 'none')[] {
  const results: ('win' | 'loss' | 'none')[] = recentPlays.slice(0, count).map(play => {
    const r = getPlayResult(play, playerId);
    return r === 'unknown' ? 'none' : r;
  });
  while (results.length < count) results.push('none');
  return results;
}

export function computeWinStreak(recentPlays: PlayOutput[], playerId: string): { type: 'win'; count: number } | null {
  if (recentPlays.length === 0) return null;
  const firstResult = getPlayResult(recentPlays[0], playerId);
  if (firstResult !== 'win') return null;

  let count = 0;
  for (const play of recentPlays) {
    if (getPlayResult(play, playerId) === 'win') count++;
    else break;
  }
  return count >= 2 ? { type: 'win', count } : null;
}

const toRankable = (p: LeaguePlayer) => ({
  playerId: p.playerId,
  name: p.playerName,
  rating: p.rating,
  sigma: p.sigma,
  gamesPlayed: p.gamesPlayed,
});

/**
 * League position (1-based) before the play, ordered like the league page: conservative
 * rating (mu - 3 sigma), highest first. The player's mu and sigma are both swapped for
 * their values before the play (never old mu with new sigma) and this play is taken off
 * their game count.
 */
export function computeLeaguePositionBefore(
  standings: LeaguePlayer[],
  playerId: string,
  before: { rating: number; sigma: number }
): number {
  const simulated = standings.map(p =>
    p.playerId === playerId
      ? { ...toRankable(p), rating: before.rating, sigma: before.sigma, gamesPlayed: Math.max(p.gamesPlayed - 1, 0) }
      : toRankable(p)
  );
  simulated.sort(comparePlayersByRank);
  const idx = simulated.findIndex(p => p.playerId === playerId);
  return idx >= 0 ? idx + 1 : standings.length;
}

/** League position (1-based) now, ordered like the league page (conservative rating). */
export function computeLeaguePositionAfter(standings: LeaguePlayer[], playerId: string): number {
  const sorted = standings.map(toRankable).sort(comparePlayersByRank);
  const idx = sorted.findIndex(p => p.playerId === playerId);
  return idx >= 0 ? idx + 1 : standings.length;
}

export function computeFunFacts(
  recentPlays: PlayOutput[],
  playerId: string,
  playerName: string,
  gameName: string,
  currentPlay: PlayOutput
): { icon: string; message: string }[] {
  const facts: { icon: string; message: string }[] = [];
  const totalPlays = recentPlays.length;

  // Play count milestones
  const milestones = [100, 75, 50, 25, 10];
  for (const m of milestones) {
    if (totalPlays === m) {
      facts.push({ icon: '🎯', message: `${playerName}'s ${m}th game of ${gameName}!` });
      break;
    }
  }

  // First win detection
  const currentResult = getPlayResult(currentPlay, playerId);
  if (currentResult === 'win') {
    const previousPlays = recentPlays.filter(p => p.id !== currentPlay.id);
    const previousWins = previousPlays.filter(p => getPlayResult(p, playerId) === 'win');
    if (previousWins.length === 0 && previousPlays.length > 0) {
      facts.push({ icon: '🏆', message: `${playerName}'s first win at ${gameName}!` });
    }
  }

  // Dominant form (4+ wins in last 5)
  const last5 = recentPlays.slice(0, 5);
  const wins = last5.filter(p => getPlayResult(p, playerId) === 'win').length;
  if (wins >= 4 && last5.length >= 5) {
    facts.push({ icon: '💪', message: `${playerName} won ${wins} of their last 5 games of ${gameName}` });
  }

  return facts;
}

export function detectUpset(
  play: PlayOutput,
  winProbabilities: Record<string, number>
): { isUpset: boolean; winnerProbability: number } {
  // Find the winning team
  const winningTeam = typeof play.ranking[0] === 'string' ? play.ranking[0] : play.ranking[0]?.[0];
  if (!winningTeam) return { isUpset: false, winnerProbability: 100 };

  // All players on the same team share the same probability (mapped from team prob)
  const winningPlayerIds = play.teams[winningTeam] || [];
  const winnerProb = winProbabilities[winningPlayerIds[0]] ?? 0;

  // Probabilities are percentages (0-100). Upset if winner had < 30%
  return {
    isUpset: winnerProb > 0 && winnerProb < 30,
    winnerProbability: winnerProb,
  };
}
